import React, { useEffect, useState } from 'react';
import '@/styles/Panels.css';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/config/firebase';

interface TasksSystemProps {
    onClose: () => void;
}

type TaskItem = {
    id: string;
    title: string;
    reward: number;
    link: string;
    status: 'available' | 'paused' | 'completed';
    order?: number;
    claimDelaySeconds?: number;
};

const getTasksCallable = httpsCallable(functions, 'getTasks');
const claimTaskCallable = httpsCallable(functions, 'claimTask');
const initiateTaskCallable = httpsCallable(functions, 'initiateTask');

const TasksSystem: React.FC<TasksSystemProps> = ({ onClose }) => {
    const [tasks, setTasks] = useState<TaskItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [claimingTaskId, setClaimingTaskId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [claimedTaskIds, setClaimedTaskIds] = useState<string[]>([]);
    const [taskProgress, setTaskProgress] = useState<Record<string, { status: string; initiatedAtMs: number | null; claimedAtMs: number | null }>>({});
    const [nowMs, setNowMs] = useState(Date.now());

    useEffect(() => {
        const loadTasks = async () => {
            setLoading(true);
            setError(null);
            try {
                const res: any = await getTasksCallable();
                setTasks((res.data?.tasks || []) as TaskItem[]);
                setClaimedTaskIds((res.data?.claimedTaskIds || []) as string[]);
                setTaskProgress((res.data?.taskProgress || {}) as Record<string, { status: string; initiatedAtMs: number | null; claimedAtMs: number | null }>);
            } catch (e: any) {
                setError(e?.message || 'Failed to load tasks.');
            } finally {
                setLoading(false);
            }
        };

        loadTasks();
    }, []);

    useEffect(() => {
        const t = window.setInterval(() => setNowMs(Date.now()), 1000);
        return () => window.clearInterval(t);
    }, []);

    const initiateTask = async (taskId: string) => {
        setError(null);
        try {
            await initiateTaskCallable({ taskId });
            setTaskProgress((prev) => ({
                ...prev,
                [taskId]: {
                    ...(prev[taskId] || { claimedAtMs: null }),
                    status: 'initiated',
                    initiatedAtMs: Date.now(),
                },
            }));
        } catch (e: any) {
            setError(e?.message || 'Failed to initiate task.');
        }
    };

    const claimTask = async (taskId: string) => {
        setClaimingTaskId(taskId);
        setError(null);
        try {
            await claimTaskCallable({ taskId });
            setClaimedTaskIds((prev) => [...new Set([...prev, taskId])]);
        } catch (e: any) {
            setError(e?.message || 'Failed to claim task.');
        } finally {
            setClaimingTaskId(null);
        }
    };

    const handleOpenTask = (task: TaskItem, isClaimed: boolean, isInitiated: boolean) => {
        if (!task.link) return;

        if (task.status === 'available' && !isClaimed && !isInitiated) {
            void initiateTask(task.id);
        }
    };

    return (
        <div className="panel tasks-panel">
            <div className="panel-header">
                <h2>Daily Tasks</h2>
                <button className="close-button" onClick={onClose}>×</button>
            </div>
            <div className="panel-content">
                {loading && <p>Loading tasks...</p>}
                {error && <p className="error-message">{error}</p>}
                {!loading && !error && <div className="task-list">
                    {tasks.map(task => (
                        (() => {
                            const progress = taskProgress[task.id];
                            const initiatedAtMs = progress?.initiatedAtMs || null;
                            const delayMs = Math.max(0, Number(task.claimDelaySeconds || 0)) * 1000;
                            const claimAvailableAtMs = initiatedAtMs ? initiatedAtMs + delayMs : null;
                            const remainingMs = claimAvailableAtMs ? Math.max(0, claimAvailableAtMs - nowMs) : null;
                            const isClaimed = claimedTaskIds.includes(task.id) || progress?.status === 'claimed';
                            const isInitiated = progress?.status === 'initiated';
                            const canClaim = task.status === 'available' && isInitiated && !isClaimed && (remainingMs === 0);

                            return (
                        <div key={task.id} className="task-item">
                            <div className="task-info">
                                <h3>{task.title}</h3>
                                <p>Reward: {task.reward} Buink</p>
                                {isInitiated && !isClaimed && remainingMs !== null && remainingMs > 0 && <p>Task in progress...</p>}
                            </div>
                            <div className="task-actions">
                                <a
                                    href={task.link || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="task-button"
                                    aria-disabled={!task.link}
                                    onClick={(e) => {
                                        if (!task.link) {
                                            e.preventDefault();
                                            return;
                                        }
                                        handleOpenTask(task, isClaimed, isInitiated);
                                    }}
                                >
                                    Open
                                </a>
                                <button
                                    className="task-button claim-btn"
                                    disabled={!canClaim || claimingTaskId === task.id}
                                    onClick={() => claimTask(task.id)}
                                >
                                    {isClaimed ? 'Claimed' : claimingTaskId === task.id ? 'Claiming...' : 'Claim'}
                                </button>
                            </div>
                        </div>
                            );
                        })()
                    ))}
                </div>}
            </div>
        </div>
    );
};

export default TasksSystem;
